import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription, timer } from 'rxjs';
import { NewRelicEventsService } from '@services/new-relic-events.service';
import { NEW_RELIC_EVENT } from '@enums/new-relic.enum';
import { ExperienceRating } from '@components/am-experience-poll/am-experience-poll.component';
import { GlobalsService } from '@services/micro/globals.service';

/**
 * Modal que contiene la encuesta de experiencia del SSBD.
 * Se cierra automáticamente después de 10 segundos enviando "excellent" por defecto,
 * o cuando el usuario selecciona una opción manualmente.
 */
@Component({
  selector: 'app-experience-poll-modal',
  templateUrl: './experience-poll-modal.component.html',
  styleUrls: ['./experience-poll-modal.component.scss'],
  standalone: false,
})
export class ExperiencePollModalComponent implements OnInit, OnDestroy {
  private readonly AUTO_CLOSE_SECONDS = 15;
  private readonly DEFAULT_RATING: ExperienceRating = 'excellent';

  private countdown = this.AUTO_CLOSE_SECONDS;
  private timerSubscription: Subscription | null = null;
  private answered = false;

  /**
   * Snapshot del reservationCode capturado al abrir el modal,
   * antes de que la navegación a '/' destruya la sesión.
   */
  readonly reservationCode: string;

  constructor(
    public activeModal: NgbActiveModal,
    private _router: Router,
    private _newRelicEventsService: NewRelicEventsService,
    private _globalsService: GlobalsService
  ) {
    // Capturar el PNR inmediatamente al construir el modal, antes de cualquier navegación
    this.reservationCode =
      this._globalsService?.getPNRLookupAWSInt()?.reservationCode ||
      this._globalsService?.getReservation()?.pnr ||
      '';
  }

  ngOnInit(): void {
    this.startAutoCloseTimer();
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }

  onRatingSelected(rating: ExperienceRating): void {
    if (this.answered) return;
    this.answered = true;
    this.stopTimer();

    setTimeout(() => {
      this.activeModal.close(rating);
      this._router.navigate(['/']);
    }, 600);
  }

  private startAutoCloseTimer(): void {
    const source = timer(1000, 1000);
    this.timerSubscription = source.subscribe(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        this.autoClose();
      }
    });
  }

  private autoClose(): void {
    if (this.answered) return;
    this.answered = true;
    this.stopTimer();

    this._newRelicEventsService.trackEvent(NEW_RELIC_EVENT.EXPERIENCE_POLL_RESPONSE, {
      rating: this.DEFAULT_RATING,
      auto_closed: true,
      reservationCode: this.reservationCode,
    });

    this.activeModal.close(this.DEFAULT_RATING);
    setTimeout(() => this._router.navigate(['/']), 1000);
  }

  private stopTimer(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = null;
    }
  }
}
